import { Button, Col, Form, Input, message, Modal, Popover, Radio, Row, Select, Space, Spin, Tag, Tooltip } from 'antd';
import { DocumentCard, PhoneInput } from 'cmt-ui-kit';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCookie } from '../../../services/cookies';
import { getBlocks, getServices } from '../../../services/general';
import { overcapacityCheck } from '../../../services/reservation';
import evaluationTypes from "../../../utils/evaluationType.json";
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, hasEnoughSearchLength } from '../../../utils/searchConfig';
import usePatient from '../hooks/usePatient';
import useProfessionField from '../hooks/useProfessionField';

const { Option } = Select;
const { format } = require('rut.js');

const PatientFormCL = ({
  index,
  patient,
  updatePatientAttribute,
  updatePatientServices,
  repeatServicesForAllPatients,
  repeatLastPatientServices,
  patients,
  totalPatients,
  type,
  reservationForm,
  countryCode = 'CL',
  repeatBlocksForAllPatients,
  setDisabled,
  countrySettings,
}) => {
  const [loading, setLoading] = useState(false);
  const [overCapacityLoading, setOverCapacityLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [isOverCapacityModalVisible, setIsOverCapacityModalVisible] = useState();
  const [currentBlock, setCurrentBlock] = useState();
  const [form] = Form.useForm();
  const [overCapacityForm] = Form.useForm();
  const [services, setServices] = useState([]);
  const [blocks, setBlocks] = useState();
  const [extraServiceData, setExtraServiceData] = useState([]);
  const [documentType, setDocumentType] = useState('rut');
  const { t } = useTranslation();
  const searchPatient = usePatient(form, updatePatientAttribute, index, setDisabled, patients);
  const { isProfessionRequired, isProfessionEditable } = useProfessionField(form, 'CL', evaluationTypes);

  const handleOnBlur = async (event) => {
    let inputValue = event.target.value;
    if (patients.filter(item => item.document_number === inputValue).length <= 1) {
      await searchPatient.getPatient(inputValue, documentType);
    } else {
      message.error({ content: 'Error. El paciente ya existe en esta pre admisión', style: { marginTop: '45vh', }, })
      form.resetFields();
      form.setFieldsValue({ 'documentType': documentType });
    }
  }

  useEffect(() => {
    if (searchPatient.data) {
      updatePatientAttribute(index, 'profession', searchPatient.data.position);
      updatePatientAttribute(index, 'names', searchPatient.data.names);
      updatePatientAttribute(index, 'last_name', searchPatient.data.last_name);
      updatePatientAttribute(index, 'email', searchPatient.data.email);
      updatePatientAttribute(index, 'phone', searchPatient.data.phone);
    }
  }, [searchPatient.data, form])

  const onFieldsChange = (values) => {
    try {
      if (values[0].name[0] !== 'prestations') {
        if (values[0].name[0] === 'block_id') {
          const filterBlockName = blocks.find(block => block.id === values[0].value);
          updatePatientAttribute(index, values[0].name[0], values[0].value);
          updatePatientAttribute(index, 'block', filterBlockName['block']);
        } else {
          updatePatientAttribute(index, values[0].name[0], values[0].value);
        }
      } else if (extraServiceData && extraServiceData.length > 0) {
        let refactorServices = [];
        extraServiceData.forEach(item => {
          if (item?.value) {
            const id = item.value.split('-')[0];
            refactorServices.push(
              {
                'id': id && parseInt(id, 10),
                'name': item.children,
                'type': item.type,
                'sale_amount': item.price,
                'currency_id': item.currency_id,
                'department_id': item.department_id,
                'description': item.description,
              }
            );
            if (item.attached_services?.length > 0 && countrySettings?.addAttachedServices?.['CL']) {
              item.attached_services.forEach(attached => {
                if (!refactorServices.some(service => service.id === attached.id && service.type === attached.type)) {
                  refactorServices.push(
                    {
                      'id': attached.id,
                      'name': attached.name,
                      'type': attached.type,
                      'sale_amount': attached.sale_amount,
                      'description': attached.description,
                      'department_id': attached.department_id,
                    }
                  )
                }
              })
            }
          }
        });
        updatePatientServices(index, values[0].name[0], refactorServices);
        setExtraServiceData([]);
      }
    } catch (error) {
      console.error(error);
      message.error('Ha ocurrido un error al actualizar los datos del paciente');
    }
  };

  const fetchServices = debounce(async (name) => {
    setLoadingServices(true);
    try {
      const response = hasEnoughSearchLength(name) ?
        await getServices({ query: name, limit: 50, branch_office_id: reservationForm.getFieldValue('branch_office_id') })
        :
        { data: [] };
      setServices([...response.data, ...services]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingServices(false);
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS);

  const handleOnChangeSearch = (field, value, option) => {
    if (value) {
      form.setFieldsValue({ [field]: value });
      setExtraServiceData(option);
    } else {
      form.setFieldsValue({ [field]: null });
      setExtraServiceData([]);
      setServices([]);
    }
  };

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const reservationDate = reservationForm.getFieldValue('reservation_date');
      const branchOfficeId = reservationForm.getFieldValue('branch_office_id');
      const departmentId = getCookie('department_id');
      if (departmentId && reservationDate && branchOfficeId) {
        const response = await getBlocks({
          branchOffice: branchOfficeId,
          date: reservationDate.format('YYYY-MM-DD'),
          department: departmentId,
        });
        setBlocks(response.blocks);
        if (patient.block_id && blocks) {
          handleCancel();
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const capacityByBlock = (block) =>
    block.max_capacity - map(patients, 'block_id').filter(blockId => blockId === block.id).length;

  const handleRepeatBlocksForAll = () => {
    const withoutOrDifferentBlocks = patients.filter(p => (!p.block || p.block_id !== patients[0].block_id)).length;

    if (capacityByBlock(currentBlock) >= withoutOrDifferentBlocks) {
      repeatBlocksForAllPatients();
    } else {
      message.info({
        content: t('reservations.patientForm.repeatBlockForAllDisclaimer'),
        style: {
          marginTop: '45vh',
        },
        duration: 5,
      });
    }
  };

  const handleBlock = (event, option) => {
    setCurrentBlock(option.block);
    if (capacityByBlock(option.block) < 0) {
      overCapacityForm.resetFields();
      setIsOverCapacityModalVisible(true);
    }
  };

  const handleCancel = () => {
    overCapacityForm.resetFields();
    setIsOverCapacityModalVisible(false);
    updatePatientAttribute(index, 'block_id', null);
    updatePatientAttribute(index, 'block', null);
    setCurrentBlock(null);
  };

  const handleOk = () => {
    setOverCapacityLoading(true);
    overCapacityForm
      .validateFields()
      .then(values => {
        overcapacityCheck(values)
          .then(response => {
            if (response.length) {
              overCapacityForm.resetFields();
              setIsOverCapacityModalVisible(false);
              setCurrentBlock(null);
              updatePatientAttribute(index, 'over_capacity', true);
              message.success({
                content: 'Código validado exitosamente.',
                style: {
                  marginTop: '35vh',
                },
              });
            } else {
              message.error({
                content: 'Código inválido. Intente nuevamente.',
                style: {
                  marginTop: '35vh',
                },
              });
            }
          })
          .catch(err => {
            message.error({
              content: 'No hemos podido validar este código. Intente nuevamente.',
              style: {
                marginTop: '35vh',
              },
            });
            console.error('Error al validar código sobrecupo', err);
          })
          .finally(() => {
            setOverCapacityLoading(false);
          });
      })
  };

  const tagRender = (props) => {
    const { label } = props;
    const handleOnClose = () => {
      const filterServices = services.filter(service => service.name !== label.name).map(item => (
        {
          'id': item.id,
          'name': item.name,
          'type': item.type,
          'sale_amount': item.sale_amount,
          'currency_id': item.currency_id,
          'department_id': item.department_id,
          'description': item.description,
        }
      ))
      updatePatientServices(index, 'prestations', filterServices);
    };

    const result = label.description
      ? <Tag
        closable
        onClose={(e) => { e.stopPropagation(); handleOnClose() }}
        className='select-tag'
      >
        <p>{label.name}</p>
        <Tooltip
          title={
            <Row>
              <Col lg={24} xs={24}>
                <Row align='middle'>
                  <Col>
                    <span className='icon-alert-rounded mr-5' />
                  </Col>
                  <Col>
                    <p className='info-text mb-0'>Información</p>
                  </Col>
                </Row>
              </Col>
              <Col lg={24} xs={24}>
                <p className='mb-0' dangerouslySetInnerHTML={{ __html: label.description }} />
              </Col>
            </Row>
          }
          placement='right'
          overlayClassName='service-description-tooltip'
          autoAdjustOverflow
          color="#FFFFFF"
        >
          <span className='icon-alert-rounded ml-5' />
        </Tooltip >
      </Tag >
      : <Tag
        closable
        onClose={(e) => { e.stopPropagation(); handleOnClose() }}
        className='select-tag'
      >
        <p>{label.name}</p>
      </Tag>;

    return result;
  };

  useEffect(() => {
    if (patient) {
      setServices(patient.prestations.map(item => {
        return {
          id: item.id,
          name: item.name,
          sale_amount: item.sale_amount,
          locateCode: item.locate_code,
          currencyCode: item.currencyCode,
          countryCode: item.countryCode,
          type: item.type,
          department_id: item.department_id,
          currency_id: item.currency_id,
          description: item.description,
        }
      }));
      form.setFieldsValue({
        'id': patient.id && patient.id,
        'block_id': patient.block_id ? patient.block_id : null,
        'phone': patient.phone ? patient.phone : '',
        'profession': isProfessionEditable ? (patient.profession ?? '') : '',
        'documentType': patient.document_type ? patient.document_type : documentType,
        'documentNumber': patient.document_type === 'rut' ? format(patient.document_number) : patient.document_number,
        'names': patient.names ? patient.names : '',
        'last_name': patient.last_name ? patient.last_name : '',
        'prestations': patient.prestations?.map(service => `${service.id}-${service.type}`),
        'evaluation_type': patient.evaluation_type,
        'email': patient.email ? patient.email : '',
        'external_id': patient.external_id ? patient.external_id : '',
        'project_id': patient.project_id,
        'paid_by_patient': patient.paid_by_patient,
      });

    } else {
      form.setFieldsValue({ 'documentType': documentType });
    }
  }, [patients, totalPatients]);

  useEffect(() => {
    fetchBlocks();
  }, [
    reservationForm.getFieldValue('reservation_date'),
    reservationForm.getFieldValue('branch_office_id'),
    reservationForm.getFieldValue('prestations'),
  ]);

  useEffect(() => {
    if (!reservationForm.getFieldValue('id')) {
      updatePatientAttribute(index, 'prestations', []);
    }
  }, [reservationForm.getFieldValue('branch_office_id')]);

  return (
    <Row>
      <Col lg={24} xs={24} className='patient-form-wrapper'>
        <Modal
          className="creation-modal"
          open={isOverCapacityModalVisible}
          onCancel={handleCancel}
          closable
          footer={false}
          width={500}
        >
          <Row className='pl-20 pr-20 pt-20 pb-20'>
            <Col lg={24} xs={24}>
              <Row justify='center'>
                <Col>
                  <h1 className='text-center mb-20'>{t('reservations.overCapacityModal.title')}</h1>
                  {
                    currentBlock &&
                    <p className='mb-20'>{t('reservations.overCapacityModal.disclaimer1')} {currentBlock?.block} hs. {t('reservations.overCapacityModal.disclaimer2')}.</p>
                  }
                </Col>
                <Col lg={24} xs={24}>
                  <Form
                    form={overCapacityForm}
                    size="large"
                    className='cmt-form'
                    onFinish={handleOk}
                  >
                    <Row gutter={[0, 20]}>
                      <Col lg={24} xs={24}>
                        <Form.Item name="code" rules={[
                          { required: true, message: 'Clave sobrecupo es requerida' }
                        ]}>
                          <Input className='cmt-input' placeholder='Ingrese clave sobrecupo' />
                        </Form.Item>
                      </Col>
                      <Col lg={24} xs={24}>
                        <Row gutter={[15, 15]} justify='center'>
                          <Col>
                            <Button
                              className={overCapacityLoading ? 'cmt-button disabled outline' : 'cmt-button primary outline'}
                              type='primary'
                              size='large'
                              onClick={handleCancel}
                              disabled={overCapacityLoading}
                            >
                              Cambiar bloque
                            </Button>
                          </Col>
                          <Col>
                            <Button
                              className={overCapacityLoading ? 'cmt-button disabled' : 'cmt-button primary'}
                              type='primary'
                              size='large'
                              htmlType='submit'
                              loading={overCapacityLoading}
                              disabled={overCapacityLoading}
                            >
                              Autorizar sobrecupo
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </Col>
          </Row>
        </Modal>
        <Form form={form} layout="vertical" onFieldsChange={onFieldsChange}>
          <Form.Item name="id" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={15}>
            <Col lg={16} xs={24}>
              <Row>
                <Col lg={24} xs={24}>
                  <Spin spinning={loadingServices} className='cmt-spin'>
                    <Form.Item
                      name="prestations"
                      label={t('reservations.patientForm.servicesLabel')}
                      className={`patient-form exams select-wrapper ${((index === 0 && totalPatients > 1 && patients[index]['prestations'].length > 0) || (patients[index - 1] && patients[index - 1]['prestations'].length > 0 && index > 0)) && 'mb-0'}`}
                      rules={[{
                        required: true,
                        message: t('reservations.patientForm.servicesRule')
                      }]}
                    >
                      <Select
                        id='patientService'
                        className="cmt-select"
                        mode="multiple"
                        showArrow
                        allowClear
                        filterOption={false}
                        style={{ width: '100%' }}
                        placeholder={t('reservations.patientForm.servicePlaceholder')}
                        onSearch={fetchServices}
                        onChange={
                          (value, option) => handleOnChangeSearch('service', value, option)
                        }
                        onClear={() => setServices([])}
                        notFoundContent={null}
                        optionLabelProp="label"
                        tagRender={tagRender}
                      >
                        {
                          services?.map((service) => {
                            return (
                              <Option
                                key={service.name}
                                value={`${service.id}-${service.type}`}
                                className="cmt-select-option"
                                price={service.sale_amount}
                                type={service.type}
                                currency_code={service.currencyCode}
                                locate_code={service.locateCode}
                                country_code={service.countryCode}
                                currency_id={service.currency_id}
                                department_id={service.department_id}
                                description={service.description}
                                attached_services={service.attached_services}
                                label={{
                                  description: service.description,
                                  name: service.name,
                                  type: service.type,
                                  price: service.sale_amount,
                                }}
                              >
                                {service.name}
                              </Option>
                            )
                          })
                        }
                      </Select>
                    </Form.Item>
                  </Spin>
                </Col>
                {
                  (index === 0 && totalPatients > 1 && patients[index].prestations.length > 0) &&
                  <Col lg={24} xs={24}>
                    <Row>
                      <Col>
                        <Button
                          onClick={repeatServicesForAllPatients}
                          className="cmt-button secondary non-outline p-0"
                        >
                          <p className="mb-0" style={{ fontSize: 14, wordWrap: 'break-word' }}>
                            {t('reservations.patientForm.repeatServicesForAll')}
                          </p>
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                }
                {
                  (patients[index - 1] && patients[index - 1]['prestations'].length > 0 && index > 0) &&
                  <Col lg={24} xs={24}>
                    <Row>
                      <Col>
                        <Button
                          onClick={() => repeatLastPatientServices(index)}
                          className="cmt-button secondary non-outline p-0"
                        >
                          <p className="mb-0" style={{ fontSize: 14 }}>
                            {t('reservations.patientForm.repeatLastServices')}
                          </p>
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                }
              </Row>
            </Col>
            <Col lg={8} xs={24}>
              <Row>
                <Col lg={24} xs={24}>
                  <Form.Item
                    name="block_id"
                    label={t('reservations.patientForm.blockLabel')}
                    className={`patient-form blocks select-wrapper ${(index === 0 && totalPatients > 1 && patients[index]['block']) && 'mb-0'}`}
                    rules={[{
                      required: true,
                      message: t('reservations.patientForm.blockRule'),
                    }]}
                  >
                    {
                      loading ?
                        <Row justify="center">
                          <Spin className="custom-spinner" />
                        </Row>
                        :
                        <Select
                          onChange={handleBlock}
                          className="cmt-select"
                          placeholder={t('reservations.patientForm.blockPlaceholder')}
                        >
                          {
                            blocks?.map((block) => {
                              const capacity = capacityByBlock(block);
                              return (
                                <Option key={block.id} value={block.id} block={block}>
                                  {block.block} <Tag color={capacity > 0 ? 'green' : 'red'}>{capacity < 0 ? 0 : capacity} cupos</Tag>
                                </Option>
                              )
                            })
                          }
                        </Select>
                    }
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                {
                  (index === 0 && totalPatients > 1 && patients[index]['block']) &&
                  <Col lg={24} xs={24}>
                    <Row>
                      <Col>
                        <Button
                          onClick={() => handleRepeatBlocksForAll()}
                          className="cmt-button secondary non-outline p-0"
                        >
                          <p className="mb-0" style={{ fontSize: 14 }}>
                            {t('reservations.patientForm.repeatBlockForAll')}
                          </p>
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                }
              </Row>
            </Col>
          </Row>

          {
            (!type || countryCode === 'PE') &&
            <>
              <Row gutter={15}>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <Row>
                    <p className="text-label mb-0">Correo electrónico</p>
                  </Row>
                  <Form.Item
                    name="email"
                    rules={[
                      {
                        type: 'email',
                        message: 'El email es inválido',
                      }
                    ]}
                    size="small"
                  >
                    <Input className="cmt-input" type="email" id="emailTest" placeholder='Ingrese email' />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <Row>
                    <p className="text-label mb-0">{t('reservations.patientForm.documentTypeAndNumber')}</p>
                  </Row>
                  <Spin className='cmt-spin patient-form document-select-wrapper' spinning={searchPatient.isLoading}>
                    <DocumentCard
                      countryCode='CL'
                      setDocumentType={setDocumentType}
                      handleOnDocumentNumberBlur={handleOnBlur}
                      form={form}
                      classNameStyle='input-height'
                      type='person'
                    />
                  </Spin>
                </Col>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <Form.Item
                    name="names"
                    label="Nombres"
                    className="mb-0"
                  >
                    <Input className="cmt-input" placeholder="Ingrese los nombres" style={{ height: 32 }} />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <Form.Item
                    name="last_name"
                    label="Apellido"
                    className="mb-0"
                  >
                    <Input className="cmt-input" placeholder="Ingrese el apellido" style={{ height: 32 }} />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <PhoneInput.ReactPhone
                    countryCode='CL'
                    form={form}
                    labelText={t('reservations.patientForm.phoneLabel')}
                    optional={true}
                  />
                </Col>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <Form.Item
                    name="profession"
                    label={
                      <Space size={4}>
                        {t('reservations.patientForm.professionLabel')}
                        {!isProfessionEditable && (
                          <Popover
                            overlayClassName="profession-popover"
                            content={
                              <p className="mb-0">
                                {t('reservations.patientForm.professionPopoverDisabledText')}
                              </p>
                            }
                            placement="right"
                            trigger="hover"
                          >
                            <span className="icon-alert-rounded info-icon" />
                          </Popover>
                        )}
                      </Space>
                    }
                    className="mb-0"
                    rules={[{
                      required: isProfessionRequired,
                      message: 'Cargo es requerido'
                    }]}
                  >
                    <Input
                      className="cmt-input"
                      placeholder={isProfessionEditable ? t('reservations.patientForm.professionPlaceholder') : ''}
                      style={{ height: 32 }}
                      disabled={!isProfessionEditable}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={20} className='evaluation-type'>
                <Col lg={24} xs={24}>
                  <Form.Item
                    className="m-0 p-0"
                    name="evaluation_type"
                    label="Tipo de evaluación"
                    rules={[{ required: false }]}
                  >
                    <Radio.Group id='evaluation_type' className="cmt-radio">
                      {
                        evaluationTypes['CL'].map((evalType) =>
                          <Radio key={index - evalType.value} value={evalType.value}>{evalType.label}</Radio>
                        )
                      }
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={15} className='pb-10'>
                <Col lg={8} xs={24} style={{ marginTop: 13 }}>
                  <Form.Item
                    name="external_id"
                    label={t('reservations.patientForm.externalIdLabel')}
                    className="mb-0"
                  >
                    <Input
                      disabled
                      className="cmt-input"
                      placeholder={t('reservations.patientForm.externalIdPlaceholder')}
                      style={{ height: 32 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          }
        </Form>
      </Col>
    </Row>
  );
};

export default PatientFormCL;
